import React, { useState } from "react";
import Modal from "../shared/modal/Modal";
import { useAuth } from "../shared/auth";
import { GoogleIcon, DiscordIcon, FacebookIcon } from "./ProviderIcons";
import { inFrame, startFramedSignIn, type PopupProvider } from "../shared/auth-frame";

// Sign-in dialog. When auth isn't configured (no Supabase env vars, e.g. a dev
// server started without the .env) it just says sign-in isn't available here.
// `reason` lets the opener say WHY sign-in is being asked for (e.g. the shop's
// buy flow); the default suits the plain nav-bar Sign in click. `dim` turns on
// the regular blurred backdrop (the nav-bar opener keeps the clear one so the
// menu stays visible behind it).
export default function LoginModal({ onClose, reason, dim = false }: { onClose: () => void; reason?: string; dim?: boolean }) {
  const { enabled, signInWithGoogle, signInWithDiscord, signInWithFacebook, signInWithMagicLink } = useAuth();

  const [email, setEmail] = useState("");
  // WHO is busy, not just whether: the clicked provider gets the loading
  // treatment, the magic-link button only says "Sending…" for its own click.
  const [busyProvider, setBusyProvider] = useState<null | "google" | "discord" | "facebook" | "magic">(null);
  const busy = busyProvider !== null;
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // In another site's frame (the itch.io page): sign-in happens in a window of
  // its own (shared/auth-frame.ts); this is how to stop waiting for it.
  const [framedStop, setFramedStop] = useState<null | (() => void)>(null);

  if (!enabled) {
    return (
      <Modal
        title="Sign in"
        onClose={onClose}
        dim={dim}
        backButton
        className="app-modal--auth"
        footer={<button className="app-btn app-btn-primary" onClick={onClose}>Got it!</button>}
      >
        <p>Sign-in isn't available here right now.</p>
        <p className="app-muted">You can still play everything without an account.</p>
      </Modal>
    );
  }

  // OAuth: the page redirects away on success, so the busy overlay stays up
  // until the browser leaves; only a (rare) error clears it.
  function framed(provider: PopupProvider) {
    setError(null);
    const s = startFramedSignIn(provider);
    if (!s.opened) { setError("Allow pop-ups for this page to sign in."); return; }
    setFramedStop(() => s.stop);
    void s.done.then((r) => {
      setFramedStop(null);
      if (r.ok) onClose();
      else if (r.error) setError(`Couldn't sign in: ${r.error}`);
    });
  }

  async function oauth(provider: "google" | "discord" | "facebook", fn: () => Promise<{ error?: string }>) {
    if (inFrame) { framed(provider); return; }
    setError(null);
    setBusyProvider(provider);
    const { error } = await fn();
    if (error) {
      setError(error);
      setBusyProvider(null);
    }
  }

  async function onSendMagicLink(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setError(null);
    setBusyProvider("magic");
    const { error } = await signInWithMagicLink(email.trim());
    setBusyProvider(null);
    if (error) setError(error);
    else setSent(true);
  }

  const PROVIDER_LABEL = { google: "Google", discord: "Discord", facebook: "Facebook" } as const;
  const oauthBusy = busyProvider && busyProvider !== "magic" ? PROVIDER_LABEL[busyProvider] : null;

  if (sent) {
    return (
      <Modal
        title="Check your inbox"
        onClose={onClose}
        dim={dim}
        backButton
        className="app-modal--auth"
        footer={<button className="app-btn app-btn-primary" onClick={onClose}>Done</button>}
      >
        <p>We sent a magic sign-in link to <b>{email}</b>.</p>
        <p className="app-muted">Open it on this device to finish signing in.</p>
      </Modal>
    );
  }

  return (
    <Modal title="Sign in" onClose={onClose} dim={dim} backButton className="app-modal--auth">
      <div className="app-auth-body">
        <p className="app-muted">{reason || "Sign in to save your stats and progress."}</p>

        <div className="app-auth-providers">
          <button className="app-btn app-auth-provider app-auth-provider--google" onClick={() => oauth("google", signInWithGoogle)} disabled={busy}>
            <span className="app-auth-provider-icon"><GoogleIcon /></span>
            <span>Sign in with Google</span>
          </button>
          <button className="app-btn app-auth-provider app-auth-provider--discord" onClick={() => oauth("discord", signInWithDiscord)} disabled={busy}>
            <span className="app-auth-provider-icon"><DiscordIcon /></span>
            <span>Sign in with Discord</span>
          </button>
          <button className="app-btn app-auth-provider app-auth-provider--facebook" onClick={() => oauth("facebook", signInWithFacebook)} disabled={busy}>
            <span className="app-auth-provider-icon"><FacebookIcon /></span>
            <span>Sign in with Facebook</span>
          </button>
        </div>

        {/* (no email in another site's frame: the link would open the site
            itself, not the game in the frame) */}
        {!inFrame ? (
          <>
        <div className="app-auth-divider"><span>or</span></div>

        <form className="app-auth-magic" onSubmit={onSendMagicLink}>
          <input
            className="app-input"
            type="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            aria-label="Email address"
          />
          <button
            className={"app-btn app-btn-sm app-auth-magic-submit " + (email.trim() ? "app-btn-primary" : "app-btn-ghost")}
            type="submit"
            disabled={busy || !email.trim()}
          >
            {busyProvider === "magic" ? "Sending…" : "Send magic link"}
          </button>
        </form>
          </>
        ) : null}

        {error ? <p className="app-auth-error">{error}</p> : null}

        {/* OAuth in flight: the whole dialog goes quiet under a spinner until
            the browser leaves for the provider (or an error clears it). */}
        {framedStop ? (
          <div className="app-auth-busy" role="status">
            <span className="app-auth-spinner" aria-hidden="true" />
            <span>Finish signing in in the new window</span>
            <button className="app-btn app-btn-sm app-btn-ghost" onClick={() => { framedStop(); setFramedStop(null); }}>Cancel</button>
          </div>
        ) : null}
        {oauthBusy ? (
          <div className="app-auth-busy" role="status">
            <span className="app-auth-spinner" aria-hidden="true" />
            <span>Signing you in with {oauthBusy}…</span>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
